import React, { useState } from 'react';
import {
  Save,
  Trash2,
  Edit2,
  Check,
  X,
  Download,
  AlertCircle,
  Film,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * SavedTheatersDialog - Dialog for managing saved theater states in localStorage
 * Mirrors SavedDecksDialog pattern for Theater Mode
 */
export default function SavedTheatersDialog({
  open,
  onOpenChange,
  savedTheaters,
  onLoad,
  onSave,
  onDelete,
  onRename,
  canSaveMore,
  maxTheaters,
  currentEmpty,
}) {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const handleStartRename = (theater) => {
    setEditingId(theater.id);
    setEditName(theater.name);
  };

  const handleSaveRename = () => {
    if (editName.trim() && editingId) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveCurrentTheater = () => {
    if (saveName.trim()) {
      onSave(saveName.trim());
      setSaveName('');
      setShowSaveInput(false);
    }
  };

  const handleLoad = (theater) => {
    onLoad(theater);
    onOpenChange(false);
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="h-5 w-5" />
            {t('savedTheaters.title')}
          </DialogTitle>
          <DialogDescription>
            {t('savedTheaters.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Disclaimer Alert */}
        <Alert variant="default" className="bg-muted/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {t('savedTheaters.disclaimer')}
          </AlertDescription>
        </Alert>

        {/* Save Current Theater Section */}
        {!currentEmpty && (
          <div className="py-2 border-b">
            {showSaveInput ? (
              <div className="flex gap-2">
                <Input
                  placeholder={t('savedTheaters.namePlaceholder')}
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCurrentTheater()}
                  autoFocus
                />
                <Button size="sm" onClick={handleSaveCurrentTheater} disabled={!saveName.trim()}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowSaveInput(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => setShowSaveInput(true)}
                disabled={!canSaveMore}
              >
                <Save className="h-4 w-4" />
                {canSaveMore
                  ? t('savedTheaters.saveCurrent')
                  : t('savedTheaters.maxReached', { max: maxTheaters })}
              </Button>
            )}
          </div>
        )}

        {/* Saved Theaters List */}
        <div className="flex-1 overflow-y-auto py-2 space-y-2 min-h-[200px]">
          {savedTheaters.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Film className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>{t('savedTheaters.noTheaters')}</p>
              <p className="text-sm mt-1">
                {t('savedTheaters.noTheatersHint')}
              </p>
            </div>
          ) : (
            savedTheaters.map((theater) => (
              <div
                key={theater.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                {/* Theater Info */}
                <div className="flex-1 min-w-0">
                  {editingId === theater.id ? (
                    <div className="flex gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename();
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        autoFocus
                        className="h-8"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveRename}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelRename}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{theater.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{theater.slideCount} {theater.slideCount === 1 ? 'slide' : 'slides'}</span>
                        <span>•</span>
                        <span>{theater.totalCards || 0} cards</span>
                        <span>•</span>
                        <span>{formatDate(theater.savedAt)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                {editingId !== theater.id && (
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" onClick={() => handleLoad(theater)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedTheaters.load')}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" onClick={() => handleStartRename(theater)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedTheaters.rename')}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDelete(theater.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedTheaters.delete')}</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
